// invoke()-compatible bridge replacing the Tauri command layer (src-tauri/src/lib.rs).
//
// The React components call `invoke("command_name", { camelCaseArgs })` exactly as
// they did against Tauri; this module dispatches each command to the browser-side
// crypto / store / api modules, reproducing the Rust orchestration. Keeping the
// command names and arg/return shapes identical means the only change needed in the
// components is their import path for `invoke`.

import {
  generateGroupKey,
  generateDeviceKeypair,
  deriveSharedSecret,
  wrapGroupKey,
  unwrapGroupKey,
  encryptUpdate,
  decryptUpdate,
  b64Encode,
  b64Decode,
} from "./crypto";
import * as store from "./store";
import * as api from "./api";
import { resizeImage, encryptImageBlob } from "./imageEncrypt";
import * as Y from "yjs";
import { fromUint8Array, toUint8Array } from "js-base64";
import { maxId } from "./collectionUtils";
import { version as APP_VERSION } from "../../../package.json";

/** Stable-coded error from the bridge. Consumers can branch on `code` rather than
 * matching substrings, so wording changes never silently break call sites.
 */
export class BridgeError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "BridgeError";
    this.code = code;
  }
}

// APP_VERSION is imported from package.json (see import above) so it stays in sync
// with the declared package version automatically on each build.

// Above this many journal rows for a doc, fold them into a single snapshot on open so
// the local journal (replayed on every doc open + sync tick) stays bounded.
const COMPACT_THRESHOLD = 200;
// Matches the server's MAX_SINCE_LIMIT — a full page means there may be more to pull.
const CHAT_SINCE_PAGE = 500;
// Photo resize targets (matching the previous server-side MiniMagick targets, now
// done client-side since the server can no longer decode image bytes).
const PHOTO_FULL_MAX_DIM = 1200;
const PHOTO_THUMB_MAX_DIM = 120;
// Avatars are a single blob (no thumbnail), resized to the same 800px limit the
// old server-side MiniMagick pass used.
const AVATAR_MAX_DIM = 800;
type Args = Record<string, any>;

// --- Credential helpers (mirror require_auth / require_group_key / require_credentials) ---

async function requireAuth(): Promise<string> {
  const token = await store.loadAuthToken();
  if (!token) throw new Error("Not authenticated");
  return token;
}

async function requireServerFamilyId(): Promise<number> {
  const id = await store.kvGet<number>("server_family_id");
  if (id == null) throw new Error("No server family ID");
  return id;
}

async function requireCredentials(): Promise<{ authToken: string; familyId: number; memberId: number }> {
  const memberId = await store.kvGet<number>("member_id");
  if (memberId == null) throw new Error("No member ID");
  return { authToken: await requireAuth(), familyId: await requireServerFamilyId(), memberId };
}

async function requireDevicePrivateKey(): Promise<Uint8Array> {
  const k = await store.kvGet<Uint8Array>("device_private_key");
  if (!k) throw new Error("No device private key stored");
  return k;
}

function req<T>(value: T | undefined, name: string): T {
  if (value === undefined) throw new BridgeError("MISSING_ARG", `Missing ${name}`);
  return value;
}

const channelOf = (args: Args): string => (args.channel as string) ?? "family";

// Encrypt/decrypt short metadata strings (e.g. doc titles) with the group key, reusing
// the parity-locked AEAD. Keeps human-readable titles off the server (Phase 4).
const encryptText = (groupKey: Uint8Array, text: string): Promise<string> =>
  encryptUpdate(groupKey, b64Encode(new TextEncoder().encode(text)));
const decryptText = async (groupKey: Uint8Array, ct: string): Promise<string> =>
  new TextDecoder().decode(b64Decode(await decryptUpdate(groupKey, ct)));

// Decrypt a member's name for the local roster, falling back to a neutral label if it
// hasn't been backfilled yet (Phase 4b).
const resolveMemberName = async (groupKey: Uint8Array, ct: string | null): Promise<string> => {
  if (!ct) return "Member";
  try {
    return await decryptText(groupKey, ct);
  } catch {
    return "Member";
  }
};

// Decrypt a doc title, falling back to a neutral label for legacy rows whose title was never
// encrypted (pre-Phase-4, dropped by migration) or fails to decrypt. A failed *title* must never
// halt the sync stream — only the encrypted update payload matters for correctness.
const resolveDocTitle = async (groupKey: Uint8Array, ct: string | null): Promise<string> => {
  if (!ct) return "Untitled";
  try {
    return await decryptText(groupKey, ct);
  } catch {
    return "Untitled";
  }
};

// How long to wait for a peer to fulfill a resync before giving up and fast-forwarding the cursor
// to the relay's purge floor (resuming live sync). The purged history is unrecoverable without a
// peer anyway, so a peer-less device should not stay blocked from new updates indefinitely.
const RESYNC_PEER_GRACE_MS = 3 * 60 * 1000;

// Reserved doc_type marking a deletion in the sync journal. Documents have no server-side record
// to delete, so we propagate deletes as an ordinary (opaque) update whose type signals "removed";
// peers drop the doc on pull. The relay already sees every doc's type/id, so this leaks nothing new.
const DELETED_DOC_TYPE = "__deleted__";

async function clearResyncState(): Promise<void> {
  await store.kvSet("resync_needed", false);
  await store.kvSet("resync_request_id", null);
  await store.kvSet("resync_since", null);
  await store.kvSet("resync_floor", null);
}

// --- App version / update detection -------------------------------------
// The running bundle's content hash, parsed from the entrypoint module URL (set at startup by
// inertia.tsx). The server reports its *current* entry path; a mismatch means a newer build has
// shipped since this client loaded, and the "check_update" command offers a reload.
//
// CONTRACT: Vite names the entry chunk "inertia-<hash>.js" (configured in vite.config.ts via
// vite-plugin-ruby). If that naming convention ever changes, update the regex below.
// Returns null (safe fallback) when the path does not match so update detection degrades
// gracefully rather than breaking.
const buildIdFromPath = (path: string | null): string | null => {
  const m = path?.match(/inertia-([A-Za-z0-9_-]+)\.js/);
  return m ? m[1] : null;
};
let runningBuildId: string | null = null;
export function setRunningBuild(entryUrl: string): void {
  runningBuildId = buildIdFromPath(entryUrl);
}

export type SubscriptionResult = {
  enabled: boolean;
  tier: "free" | "duo" | "family";
  status: string;
  source: "stripe" | "promo" | null;
  autoRenew: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  originalPurchasedAt: string | null;
  canceledAt: string | null;
  memberCount: number;
  memberLimit: number;
  prices: { duo: number; family: number };
  message: string | null;
};

function mapSubscription(s: api.SubscriptionResponse): SubscriptionResult {
  return {
    enabled: s.enabled,
    tier: s.tier,
    status: s.status,
    source: s.source,
    autoRenew: s.auto_renew,
    currentPeriodStart: s.current_period_start,
    currentPeriodEnd: s.current_period_end,
    originalPurchasedAt: s.original_purchased_at,
    canceledAt: s.canceled_at,
    memberCount: s.member_count,
    memberLimit: s.member_limit,
    prices: s.prices,
    message: s.message ?? null,
  };
}

// --- Snapshot helpers ---

/**
 * Encrypt and serialize a batch of plain-text snapshots into `SnapshotEntry[]` for the relay.
 * Used by `push_snapshots` (invite flow) and `fulfill_resync` (peer handoff) to avoid
 * duplicating the same encrypt-title + encrypt-snapshot + stamp-sequence loop.
 */
export async function buildSnapshotEntries(
  groupKey: Uint8Array,
  snapshots: Array<{ docId: string; docType: string; docTitle: string; updateB64: string }>,
  sequenceAt: number,
): Promise<api.SnapshotEntry[]> {
  const entries: api.SnapshotEntry[] = [];
  for (const s of snapshots) {
    entries.push({
      doc_id: s.docId,
      doc_type: s.docType,
      encrypted_title: await encryptText(groupKey, s.docTitle),
      encrypted_snapshot: await encryptUpdate(groupKey, s.updateB64),
      sequence_at: sequenceAt,
    });
  }
  return entries;
}

/**
 * Validate, store, and register a batch of encrypted snapshots received from the relay.
 * Each entry is decrypted as a validation step (throws on tampered/wrong-key ciphertext),
 * inserted into the local update journal, and upserted into the document index with its
 * decrypted title.
 *
 * Returns the highest `sequence_at` seen across all entries, starting from `initialMaxSeq`.
 * Pass the current cursor value for resync flows; pass 0 for fresh-join flows where the
 * caller only advances the cursor when the result is positive.
 *
 * Used by `check_join_status`, `pull_sync_updates`, and `ensure_resync`.
 */
export async function applySnapshots(
  groupKey: Uint8Array,
  snapshots: api.SnapshotEntry[],
  initialMaxSeq = 0,
): Promise<number> {
  let maxSeq = initialMaxSeq;
  for (const s of snapshots) {
    await decryptUpdate(groupKey, s.encrypted_snapshot); // validate decryptability
    await store.insertUpdate(s.doc_id, s.encrypted_snapshot);
    await store.upsertDocumentIgnore(s.doc_id, s.doc_type, await resolveDocTitle(groupKey, s.encrypted_title));
    if (s.sequence_at > maxSeq) maxSeq = s.sequence_at;
  }
  return maxSeq;
}

/**
 * Handle a 409 "resync required" error from the relay. Records the resync flags
 * (resync_needed, resync_floor, resync_since) so that ensure_resync can initiate
 * peer-handoff recovery on the next tick. Returns true when the error was a
 * recognized 409 resync signal (caller should return early); false otherwise
 * (caller should rethrow).
 *
 * Exported so that unit tests can exercise this path without going through the
 * full pull_sync_updates invoke() call.
 */
export async function handleResyncRequired(e: unknown): Promise<boolean> {
  const body = e instanceof api.ApiError && e.status === 409
    ? (e.body as { resync_required?: boolean; purged_through?: number } | undefined)
    : undefined;
  if (!body?.resync_required) return false;

  await store.kvSet("resync_needed", true);
  // Remember the relay's purge floor + when we first got stuck, so ensure_resync can
  // fast-forward to it if no peer ever fulfills (see RESYNC_PEER_GRACE_MS).
  if (typeof body.purged_through === "number") await store.kvSet("resync_floor", body.purged_through);
  if (!(await store.kvGet<number>("resync_since"))) await store.kvSet("resync_since", Date.now());
  console.warn("[sync] relay requires resync (cursor behind purge floor); awaiting peer handoff");
  return true;
}

/**
 * Apply a batch of fulfilled resync snapshots: validate, store, and upsert each
 * entry, then return the highest sequence seen alongside the total count applied.
 * Wraps applySnapshots with a structured return value suited to the ensure_resync
 * fulfilled-state path.
 *
 * Exported so that unit tests can exercise this path without going through the
 * full ensure_resync invoke() call.
 */
export async function applyFulfilledSnapshots(
  groupKey: Uint8Array,
  snapshots: api.SnapshotEntry[],
  initialSeq: number,
): Promise<{ maxSeq: number; applied: number }> {
  const maxSeq = await applySnapshots(groupKey, snapshots, initialSeq);
  return { maxSeq, applied: snapshots.length };
}

// --- Private helpers (not part of the invoke API) ---

/** Builds an invite or device-link QR payload. Shared by get_invite_code and
 * get_device_link_code, which differ only in the deviceLink flag and the
 * resulting `&link=1` suffix. */
async function buildInviteCode(deviceLink: boolean) {
  const token = await requireAuth();
  const resp = await api.createInvite(token, await requireServerFamilyId(), deviceLink);
  const familyId = (await store.kvGet<string>("family_id")) ?? "";
  const familyName = (await store.kvGet<string>("family_name")) ?? "";
  const data = b64Encode(new TextEncoder().encode(JSON.stringify({ f: familyId, n: familyName })));
  // Real https URL now (was the familyapp:// scheme under Tauri) — scans straight into the PWA.
  const linkSuffix = deviceLink ? "&link=1" : "";
  const payload = `${location.origin}/invite?code=${resp.code}&d=${data}${linkSuffix}`;
  return { payload, code: resp.code, expiresAt: resp.expires_at };
}

/**
 * Dispatch one sync update from the relay to the appropriate local handler.
 * Returns a result entry (or null to skip) for each of the four cases:
 *   - __deleted__ doc_type: tombstone the doc and emit a deleted marker
 *   - already-deleted doc: ignore (delete wins)
 *   - normal update: validate/store via applySnapshots and emit the plaintext
 */
async function dispatchSyncUpdate(
  groupKey: Uint8Array,
  u: { doc_id: string; doc_type: string; encrypted_title: string | null; encrypted_update: string; sequence: number },
): Promise<{ docId: string; updateB64: string; deleted?: boolean } | null> {
  if (u.doc_type === DELETED_DOC_TYPE) {
    // A peer deleted this doc: drop it locally and tombstone so a later edit can't revive it.
    await store.markDocumentDeleted(u.doc_id);
    return { docId: u.doc_id, updateB64: "", deleted: true };
  }
  if (await store.isDocumentDeleted(u.doc_id)) {
    // Late/racing edit for a doc we've already deleted: ignore it (delete wins).
    return null;
  }
  // Adapt the live-update shape to the canonical SnapshotEntry interface, then
  // delegate to applySnapshots for the consistent validate + insert + upsert path.
  // Sequence tracking is handled by the outer loop; pass 0 so applySnapshots
  // does not interfere with the loop's own maxSeq accounting.
  await applySnapshots(groupKey, [{
    doc_id: u.doc_id,
    doc_type: u.doc_type,
    encrypted_title: u.encrypted_title,
    encrypted_snapshot: u.encrypted_update,
    sequence_at: 0,
  }]);
  return { docId: u.doc_id, updateB64: await decryptUpdate(groupKey, u.encrypted_update) };
}

/**
 * Return true when the peer-handoff grace window has expired and the cursor
 * should be fast-forwarded to the relay's purge floor so live pulls resume.
 */
function isGraceWindowExpired(since: number | null | undefined, floor: number | null | undefined): boolean {
  return typeof since === "number" && typeof floor === "number" && Date.now() - since > RESYNC_PEER_GRACE_MS;
}

// Resize an image to the avatar max dimension, encrypt it with the group key, and
// POST the single ciphertext blob to the avatar endpoint. Shared by upload_avatar
// (a freshly picked file) and backfill_own_avatar (a re-fetched legacy avatar) so the
// resize + encrypt + upload contract lives in exactly one place and neither path can
// drift into a different wire format or a second POST.
async function encryptAndUploadAvatar(
  authToken: string,
  familyId: number,
  memberId: number,
  groupKey: Uint8Array,
  file: File | Blob,
): Promise<void> {
  const resized = await resizeImage(file, AVATAR_MAX_DIM);
  const ciphertext = await encryptImageBlob(groupKey, resized);
  await api.uploadAvatar(authToken, familyId, memberId, b64Encode(ciphertext));
}

// --- Command handlers ---

const handlers: Record<string, (args: Args) => Promise<unknown>> = {
  // Family lifecycle ------------------------------------------------------
  async get_family_status() {
    if (!(await store.hasKeys())) return null;
    const pub = await store.kvGet<Uint8Array>("device_public_key");
    return {
      familyId: (await store.kvGet<string>("family_id")) ?? "",
      familyName: (await store.kvGet<string>("family_name")) ?? "",
      memberName: (await store.kvGet<string>("member_name")) ?? "",
      memberId: (await store.kvGet<number>("member_id")) ?? 0,
      publicKey: pub ? b64Encode(pub) : "",
    };
  },

  async init_family(args) {
    const groupKey = generateGroupKey();
    const { privateKey, publicKey } = generateDeviceKeypair();
    const familyId = crypto.randomUUID();
    const publicKeyB64 = b64Encode(publicKey);

    const resp = await api.createFamily(req(args.familyName, "familyName"), req(args.memberName, "memberName"), publicKeyB64);

    await store.kvSet("group_key", groupKey);
    await store.kvSet("device_private_key", privateKey);
    await store.kvSet("device_public_key", publicKey);
    await store.kvSet("family_id", familyId);
    await store.kvSet("family_name", args.familyName as string);
    await store.kvSet("member_name", args.memberName as string);
    await store.storeServerCredentials(resp.auth_token, resp.family_id, resp.member_id, resp.device_id);

    return {
      familyId,
      familyName: args.familyName,
      memberName: args.memberName,
      memberId: resp.member_id,
      publicKey: publicKeyB64,
    };
  },

  async get_invite_code() {
    return buildInviteCode(false);
  },

  // Like get_invite_code, but the token links a new device to *this* member's
  // identity (the joiner becomes another device of you, not a new member). The
  // `&link=1` marker lets the receiving device skip the "what's your name?" step.
  async get_device_link_code() {
    return buildInviteCode(true);
  },

  async redeem_invite_code(args) {
    const resp = await api.previewInvite(String(req(args.code, "code")).toUpperCase());
    return {
      familyName: resp.family_name,
      members: resp.members.map((m) => ({ id: m.id, displayName: "" })),
      expiresAt: resp.expires_at,
      deviceLink: resp.device_link ?? false,
      linkMemberName: resp.link_member_name ?? null,
    };
  },

  async join_family(args) {
    const { privateKey, publicKey } = generateDeviceKeypair();
    const publicKeyB64 = b64Encode(publicKey);
    // For a device link the server fills in the member name from the existing
    // identity; the approved name lands locally via check_join_status.
    const memberName = (args.memberName as string | undefined) ?? "";
    const resp = await api.joinInvite(req(args.code, "code"), memberName, publicKeyB64);
    await store.kvSet("device_private_key", privateKey);
    await store.kvSet("device_public_key", publicKey);
    if (memberName) await store.kvSet("member_name", memberName);
    // The server gates the approval poll on this secret (404 without it), so stash
    // it next to the pending join state; check_join_status replays it on each poll.
    await store.kvSet("join_claim_token", resp.claim_token);
    return { status: resp.status, joinRequestId: resp.join_request_id, familyName: resp.family_name };
  },

  async check_join_status(args) {
    const claimToken = (await store.kvGet<string>("join_claim_token")) ?? "";
    const resp = await api.checkJoinStatus(args.joinRequestId, claimToken);
    if (resp.status !== "approved") return { status: resp.status, familyInfo: null };

    const authToken = req(resp.auth_token, "auth_token");
    const serverFamilyId = req(resp.family_id, "family_id");
    const memberId = req(resp.member_id, "member_id");
    const deviceId = req(resp.device_id, "device_id");
    const familyName = resp.family_name ?? "";
    const memberName = resp.member_name ?? "";

    const familyId = crypto.randomUUID();
    await store.kvSet("family_id", familyId);
    await store.kvSet("family_name", familyName);
    await store.kvSet("member_name", memberName);
    await store.storeServerCredentials(authToken, serverFamilyId, memberId, deviceId);

    // Unwrap the group key via X25519 ECDH with the approver.
    const approverPub = b64Decode(req(resp.approver_public_key, "approver_public_key"));
    const shared = deriveSharedSecret(await requireDevicePrivateKey(), approverPub);
    const groupKey = await unwrapGroupKey(req(resp.wrapped_key, "wrapped_key"), shared);
    await store.kvSet("group_key", groupKey);

    const snapshots = resp.snapshots ?? [];
    if (snapshots.length > 0) {
      const maxSeq = await applySnapshots(groupKey, snapshots);
      if (maxSeq > 0) await store.updateSyncCursor(maxSeq);
    }

    // Credentials claimed; the token is single-use server-side, so drop it locally.
    await store.kvDelete("join_claim_token");

    const pub = await store.kvGet<Uint8Array>("device_public_key");
    return {
      status: "approved",
      familyInfo: { familyId, familyName, memberName, memberId, publicKey: pub ? b64Encode(pub) : "" },
    };
  },

  async leave_family() {
    try {
      const c = await requireCredentials();
      await api.deleteMember(c.authToken, c.familyId, c.memberId);
    } catch {
      /* best-effort, like the Rust path */
    }
    await store.clearAll();
  },

  // Wipe all local state without contacting the server. Used when the server has
  // already removed this device (booted from the family on downgrade) — a
  // deleteMember call would just 401, so we only clear local storage.
  async force_logout() {
    await store.clearAll();
  },

  // Members / join requests ----------------------------------------------
  async list_members() {
    const groupKey = await store.loadGroupKey();
    const members = await api.listMembers(await requireAuth(), await requireServerFamilyId());
    return Promise.all(members.map(async (m) => ({
      id: m.id,
      displayName: await resolveMemberName(groupKey, m.encrypted_display_name),
      role: m.role,
      online: m.online,
      lastActiveAt: m.last_active_at,
      isSelf: m.is_self,
      avatarUrl: m.avatar_url,
      avatarEncrypted: m.avatar_encrypted,
    })));
  },

  // Roster for chat/notification attribution: member id → decrypted name. Sender
  // names are no longer on the wire (Phase 4b); clients resolve them locally.
  async get_member_names() {
    const groupKey = await store.loadGroupKey();
    const members = await api.listMembers(await requireAuth(), await requireServerFamilyId());
    const out: Record<number, string> = {};
    for (const m of members) out[m.id] = await resolveMemberName(groupKey, m.encrypted_display_name);
    return out;
  },

  // On-connect self-backfill: ensure our own member name is stored encrypted. Covers
  // founder, joiner, and legacy members uniformly. For a device link (member already
  // encrypted by another device), just resolve our name into local storage.
  async sync_member_name() {
    const { authToken, familyId, memberId } = await requireCredentials();
    const groupKey = await store.loadGroupKey();
    const members = await api.listMembers(authToken, familyId);
    const self = members.find((m) => m.is_self);
    if (self?.encrypted_display_name) {
      try { await store.kvSet("member_name", await decryptText(groupKey, self.encrypted_display_name)); } catch { /* keep local */ }
      return;
    }
    const name = await store.kvGet<string>("member_name");
    if (name) await api.updateMember(authToken, familyId, memberId, await encryptText(groupKey, name));
  },

  async update_member_name(args) {
    const { authToken, familyId, memberId } = await requireCredentials();
    const groupKey = await store.loadGroupKey();
    await api.updateMember(authToken, familyId, memberId, await encryptText(groupKey, req(args.newName, "newName")));
    await store.kvSet("member_name", args.newName);
  },

  // Shared, family-wide chat retention (days). Any member may read or change it.
  async get_chat_retention() {
    const s = await api.getFamilySettings(await requireAuth(), await requireServerFamilyId());
    return s.chat_retention_days;
  },

  async set_chat_retention(args) {
    const days = Number(args.days);
    if (!Number.isInteger(days) || days <= 0) {
      throw new Error("Retention must be a whole number of days greater than 0");
    }
    const s = await api.updateFamilySettings(await requireAuth(), await requireServerFamilyId(), days);
    return s.chat_retention_days;
  },

  async list_join_requests() {
    const reqs = await api.listJoinRequests(await requireAuth(), await requireServerFamilyId());
    return reqs.map((r) => ({
      id: r.id,
      memberName: r.member_name,
      publicKey: r.public_key,
      deviceLink: r.device_link ?? false,
      createdAt: r.created_at,
    }));
  },

  async approve_join_request(args) {
    const token = await requireAuth();
    const familyId = await requireServerFamilyId();
    const groupKey = await store.loadGroupKey();
    const joinerPub = b64Decode(req(args.joinerPublicKeyB64, "joinerPublicKeyB64"));
    const shared = deriveSharedSecret(await requireDevicePrivateKey(), joinerPub);
    const wrapped = await wrapGroupKey(groupKey, shared);
    const myPub = await store.kvGet<Uint8Array>("device_public_key");
    if (!myPub) throw new BridgeError("MISSING_ARG", "No device public key");
    await api.approveJoinRequest(token, familyId, req(args.requestId, "requestId"), wrapped, b64Encode(myPub));
  },

  async reject_join_request(args) {
    await api.rejectJoinRequest(await requireAuth(), await requireServerFamilyId(), req(args.requestId, "requestId"));
  },

  // Documents (local) -----------------------------------------------------
  async create_document(args) {
    return store.createDocument(req(args.docType, "docType"), req(args.title, "title"));
  },
  async list_documents(args) {
    return store.listDocuments(args.docType);
  },
  async rename_document(args) {
    await store.renameDocument(req(args.docId, "docId"), req(args.title, "title"));
  },
  async delete_document(args) {
    const groupKey = await store.loadGroupKey();
    // Tombstone locally first so the doc can't be resurrected by a racing edit, then propagate
    // the deletion through the sync journal as a reserved-type update (best-effort; queued offline).
    await store.markDocumentDeleted(req(args.docId, "docId"));

    const token = await store.loadAuthToken();
    if (token) {
      const payload = await encryptUpdate(groupKey, ""); // content irrelevant; the type carries the signal
      const encryptedTitle = await encryptText(groupKey, "");
      try {
        await api.pushSync(token, args.docId, DELETED_DOC_TYPE, encryptedTitle, payload);
      } catch {
        await store.enqueueOutbox({ docId: args.docId, docType: DELETED_DOC_TYPE, encryptedTitle, encryptedB64: payload });
      }
    }
  },

  // CRDT sync -------------------------------------------------------------
  async save_update(args) {
    const groupKey = await store.loadGroupKey();
    const encrypted = await encryptUpdate(groupKey, req(args.updateB64, "updateB64"));
    await store.insertUpdate(req(args.docId, "docId"), encrypted);

    const token = await store.loadAuthToken();
    if (token) {
      const [docType, docTitle] = await store.docMetadata(args.docId);
      const encryptedTitle = await encryptText(groupKey, docTitle);
      try {
        await api.pushSync(token, args.docId, docType, encryptedTitle, encrypted);
      } catch {
        // Offline: queue for retry on reconnect (see flushOutbox).
        await store.enqueueOutbox({ docId: args.docId, docType, encryptedTitle, encryptedB64: encrypted });
      }
    }
  },

  async load_updates(args) {
    const groupKey = await store.loadGroupKey();
    const blobs = await store.loadUpdateBlobs(req(args.docId, "docId"));
    const out: string[] = [];
    for (const blob of blobs) out.push(await decryptUpdate(groupKey, blob));
    return out;
  },

  async pull_sync_updates() {
    const token = await requireAuth();
    const groupKey = await store.loadGroupKey();
    const after = await store.readSyncCursor();

    let updates: Awaited<ReturnType<typeof api.pullSync>>;
    try {
      updates = await api.pullSync(token, after);
    } catch (e) {
      // The relay purged updates this device never received; a normal pull can't
      // recover them. Flag for peer-handoff resync (Phase 1b) rather than silently
      // applying a partial stream on top of stale state.
      if (await handleResyncRequired(e)) return [];
      throw e;
    }

    const result: { docId: string; updateB64: string; deleted?: boolean }[] = [];
    let maxSeq = after;
    for (const u of updates) {
      const entry = await dispatchSyncUpdate(groupKey, u);
      if (entry) result.push(entry);
      if (u.sequence > maxSeq) maxSeq = u.sequence;
    }
    if (maxSeq > after) await store.updateSyncCursor(maxSeq);
    return result;
  },

  // True when the server's current frontend build differs from the one this client is running
  // (a new deploy shipped since load). The UI uses this to offer a reload.
  async check_update() {
    if (!runningBuildId) return false; // unknown (dev, or unparseable) — never nag
    try {
      const latest = buildIdFromPath(await api.getBuildId());
      return latest != null && latest !== runningBuildId;
    } catch {
      return false;
    }
  },

  // Build identity for the Help screen, so each device can confirm it is running the
  // latest deploy. `build` is the running bundle's content hash (changes every deploy);
  // `upToDate` compares it to the server's current build (null = unknown, e.g. dev/offline).
  async app_version() {
    let upToDate: boolean | null = null;
    if (runningBuildId) {
      try {
        const latest = buildIdFromPath(await api.getBuildId());
        upToDate = latest == null ? null : latest === runningBuildId;
      } catch {
        upToDate = null;
      }
    }
    return { version: APP_VERSION, build: runningBuildId, upToDate };
  },

  async push_snapshots(args) {
    const token = await requireAuth();
    const groupKey = await store.loadGroupKey();
    const sequenceAt = await store.readSyncCursor();
    const entries = await buildSnapshotEntries(
      groupKey,
      args.snapshots as Array<{ docId: string; docType: string; docTitle: string; updateB64: string }>,
      sequenceAt,
    );
    await api.pushSnapshots(token, args.inviteCode, entries);
  },

  // Peer-handoff resync (Phase 1b) ----------------------------------------
  // Requester side: idempotently create a resync request and, once a peer has
  // fulfilled it, apply the fresh snapshots locally. Driven by App's sync poll and
  // nudged by the "resync_fulfilled" notification. No-op unless this device was
  // flagged behind (resync_needed, set when a pull hit 409) or has a request open.
  async ensure_resync() {
    const needed = await store.kvGet<boolean>("resync_needed");
    const id = await store.kvGet<number>("resync_request_id");
    if (!needed && !id) return { state: "idle", applied: 0 };

    const token = await requireAuth();

    // Preferred recovery: ask a peer to hand off fresh snapshots.
    if (!id) {
      const resp = await api.createResyncRequest(token);
      await store.kvSet("resync_request_id", resp.id);
      return { state: "requested", applied: 0 };
    }

    let resp: Awaited<ReturnType<typeof api.getResyncRequest>> | null = null;
    try {
      resp = await api.getResyncRequest(token, id);
    } catch (e) {
      // Request expired or was swept server-side — drop our handle so the next tick
      // re-requests from scratch (resync_needed / the grace clock are still set).
      if (e instanceof api.ApiError && e.status === 404) {
        await store.kvSet("resync_request_id", null);
      } else {
        throw e;
      }
    }

    if (resp?.status === "fulfilled") {
      const groupKey = await store.loadGroupKey();
      const initialSeq = await store.readSyncCursor();
      const { maxSeq, applied } = await applyFulfilledSnapshots(groupKey, resp.snapshots, initialSeq);
      await store.updateSyncCursor(maxSeq);
      await clearResyncState();
      return { state: "applied", applied };
    }

    // No peer fulfilled within the grace window. Stop waiting on a peer that may never come:
    // fast-forward the cursor to the relay's purge floor so live pulls resume. We forgo the purged
    // history (unrecoverable peer-less) but immediately start receiving all new updates.
    const since = await store.kvGet<number>("resync_since");
    const floor = await store.kvGet<number>("resync_floor");
    if (isGraceWindowExpired(since, floor)) {
      if ((floor as number) > (await store.readSyncCursor())) await store.updateSyncCursor(floor as number);
      await clearResyncState();
      console.warn("[sync] no peer fulfilled resync; fast-forwarded past purged history to resume live sync");
      return { state: "fast_forwarded", applied: 0 };
    }

    return { state: "waiting", applied: 0 };
  },

  // Fulfiller side: pending requests from peers (the server excludes our own).
  async list_pending_resyncs() {
    return await api.listResyncRequests(await requireAuth());
  },

  // Fulfiller side: encrypt the caller-built snapshots with the group key and post
  // them to satisfy one request. `sequence_at` is our own sync cursor — the point the
  // requester resumes pulling from.
  async fulfill_resync(args) {
    const token = await requireAuth();
    const groupKey = await store.loadGroupKey();
    const sequenceAt = await store.readSyncCursor();
    const entries = await buildSnapshotEntries(
      groupKey,
      req(args.snapshots, "snapshots") as Array<{ docId: string; docType: string; docTitle: string; updateB64: string }>,
      sequenceAt,
    );
    await api.fulfillResyncRequest(token, req(args.requestId, "requestId"), entries);
  },

  // Local journal compaction (Phase 2) ------------------------------------
  // Fold a doc's many local updates into one snapshot blob so the journal stays
  // bounded. Safe: deletes only rows captured in the snapshot (id <= maxId); updates
  // that arrive concurrently (id > maxId) survive and converge via Yjs. No server
  // contact — purely local storage hygiene. Called fire-and-forget from useYDoc.
  async compact_document(args) {
    const rows = await store.loadUpdateRows(req(args.docId, "docId"));
    if (rows.length < COMPACT_THRESHOLD) return { compacted: false };

    const groupKey = await store.loadGroupKey();
    const compactMaxId = maxId(rows);

    const ydoc = new Y.Doc();
    for (const r of rows) {
      Y.applyUpdate(ydoc, toUint8Array(await decryptUpdate(groupKey, r.updateB64)), "compact");
    }
    const snapshotPlain = fromUint8Array(Y.encodeStateAsUpdate(ydoc));
    ydoc.destroy();

    const encrypted = await encryptUpdate(groupKey, snapshotPlain);
    await store.compactDocument(args.docId, encrypted, compactMaxId);
    return { compacted: true, replaced: rows.length };
  },

  // Chat ------------------------------------------------------------------
  async send_chat_message(args) {
    const groupKey = await store.loadGroupKey();
    const encrypted = await encryptUpdate(groupKey, b64Encode(new TextEncoder().encode(req(args.message, "message"))));
    const resp = await api.sendChatMessage(await requireAuth(), encrypted, channelOf(args));
    return resp.id;
  },

  async decrypt_chat_message(args) {
    const groupKey = await store.loadGroupKey();
    const plaintextB64 = await decryptUpdate(groupKey, req(args.encryptedContent, "encryptedContent"));
    return new TextDecoder().decode(b64Decode(plaintextB64));
  },

  async load_chat_messages(args) {
    const msgs = await api.getChatMessages(await requireAuth(), args.after, 50, channelOf(args));
    return msgs.map((m) => ({
      id: m.id,
      senderMemberId: m.sender_member_id,
      encryptedContent: m.encrypted_content,
      createdAt: m.created_at,
    }));
  },

  async check_recent_chat(args) {
    const msgs = await api.getRecentChat(await requireAuth(), args.after);
    return msgs.map((m) => ({
      id: m.id,
      channel: m.channel,
      senderMemberId: m.sender_member_id,
      createdAt: m.created_at,
    }));
  },

  // Chat local cache (Phase 3) --------------------------------------------
  // Eager global pull: fetch ALL new chat across channels since our cursor, cache it
  // locally (offline history) and advance the cursor — which acks delivery server-side
  // so the relay can GC delivered chat. Paginates until drained.
  async pull_chat_messages() {
    const token = await requireAuth();
    let after = await store.readChatCursor();
    let total = 0;
    for (let page = 0; page < 20; page++) {
      const msgs = await api.getChatSince(token, after);
      if (msgs.length === 0) break;
      await store.cacheChatMessages(
        msgs.map((m) => ({
          id: m.id,
          channel: m.channel,
          senderMemberId: m.sender_member_id,
          encryptedContent: m.encrypted_content,
          createdAt: m.created_at,
          createdAtMs: Date.parse(m.created_at) || 0,
        })),
      );
      const batchMaxId = maxId(msgs, after);
      if (batchMaxId > after) {
        after = batchMaxId;
        await store.updateChatCursor(after);
      }
      total += msgs.length;
      if (msgs.length < CHAT_SINCE_PAGE) break;
    }
    return { cached: total };
  },

  // Render chat history from the local cache (works offline).
  async load_cached_chat(args) {
    const rows = await store.loadCachedChat(channelOf(args));
    return rows.map((r) => ({
      id: r.id,
      senderMemberId: r.senderMemberId,
      encryptedContent: r.encryptedContent,
      createdAt: r.createdAt,
    }));
  },

  // Persist messages seen live (realtime/poll) into the cache so they survive offline
  // and server GC even before the next global pull.
  async cache_chat_messages(args) {
    const channel = channelOf(args);
    const rows = (args.messages as Array<{
      id: number; senderMemberId: number; encryptedContent: string; createdAt: string;
    }>).map((m) => ({
      id: m.id,
      channel,
      senderMemberId: m.senderMemberId,
      encryptedContent: m.encryptedContent,
      createdAt: m.createdAt,
      createdAtMs: Date.parse(m.createdAt) || 0,
    }));
    await store.cacheChatMessages(rows);
  },

  // Client-side meaning of chat_retention_days: expire cached messages older than N days.
  async prune_chat_cache(args) {
    const days = Number(args.days);
    if (!Number.isFinite(days) || days <= 0) return;
    await store.pruneChatBefore(Date.now() - days * 86_400_000);
  },

  // Media -----------------------------------------------------------------
  // Resize the source file to the avatar max dimension, encrypt it with the group
  // key, and POST the single ciphertext blob. The server now stores it
  // synchronously (task 60), so there is no background job and no roster poll loop:
  // the avatar is stored the moment this request returns.
  async upload_avatar(args) {
    const { authToken, familyId, memberId } = await requireCredentials();
    const groupKey = await store.loadGroupKey();
    const file = req(args.file, "file") as File | Blob;

    await encryptAndUploadAvatar(authToken, familyId, memberId, groupKey, file);
  },

  // Re-encrypt this member's OWN legacy plaintext avatar in place. Older avatars
  // were resized/stored server-side as readable bytes; this fetches those current
  // (still directly fetchable) bytes, resizes + encrypts them with the group key,
  // and re-uploads through the same ciphertext avatar endpoint as upload_avatar so
  // the relay only ever holds opaque bytes afterward. It is a self-only, one-shot
  // migration gated on the avatar_encrypted flag (task 63): a member with no avatar
  // has nothing to backfill, and an already-encrypted avatar must NOT be re-fetched
  // (its bytes are ciphertext and would not decode as an image). Introduces no new
  // crypto primitive: encryption goes solely through encryptImageBlob (crypto.ts's
  // parity-locked encrypt). Returns { ok } so the caller (task 65 UI) can react.
  async backfill_own_avatar() {
    const { authToken, familyId, memberId } = await requireCredentials();
    const groupKey = await store.loadGroupKey();

    const members = await api.listMembers(authToken, familyId);
    const self = members.find((m) => m.is_self);
    if (!self?.avatar_url) return { ok: false, reason: "no_avatar" };
    if (self.avatar_encrypted) return { ok: false, reason: "already_encrypted" };

    const plaintext = await api.fetchBlob(self.avatar_url, authToken);
    const file = new Blob([plaintext]);

    await encryptAndUploadAvatar(authToken, familyId, memberId, groupKey, file);
    return { ok: true };
  },

  // Resize the source file at both the full and thumbnail max dimensions, encrypt
  // each resized blob with the group key, and POST both ciphertexts in one request.
  // The server now creates the Photo synchronously (task 58), so there is no
  // background job to poll for; one follow-up GET fetches the ready blob URLs.
  async upload_photo(args) {
    const auth = await requireAuth();
    const groupKey = await store.loadGroupKey();
    const file = req(args.file, "file") as File | Blob;

    const fullResized = await resizeImage(file, PHOTO_FULL_MAX_DIM);
    const thumbResized = await resizeImage(file, PHOTO_THUMB_MAX_DIM);

    const fullCiphertext = await encryptImageBlob(groupKey, fullResized);
    const thumbCiphertext = await encryptImageBlob(groupKey, thumbResized);

    const { processing_token } = await api.uploadPhoto(
      auth,
      b64Encode(fullCiphertext),
      b64Encode(thumbCiphertext),
    );

    const status = await api.getPhotoStatus(auth, processing_token);
    if (status.status === "ready" && status.photo_url && status.thumb_url) {
      return { photoUrl: status.photo_url, thumbUrl: status.thumb_url };
    }
    throw new Error("Photo processing failed");
  },

  // Subscription / billing ------------------------------------------------
  async get_subscription() {
    return mapSubscription(await api.getSubscription(await requireAuth()));
  },

  async get_checkout_url(args) {
    const resp = await api.createCheckout(await requireAuth(), args.tier);
    return resp.url;
  },

  async change_tier(args) {
    return mapSubscription(await api.changeTier(await requireAuth(), args.tier));
  },

  async set_auto_renew(args) {
    return mapSubscription(await api.setAutoRenew(await requireAuth(), Boolean(args.autoRenew)));
  },

  async cancel_subscription() {
    return mapSubscription(await api.cancelSubscription(await requireAuth()));
  },

  async redeem_promo_code(args) {
    return mapSubscription(await api.redeemPromo(await requireAuth(), String(args.code)));
  },

  // Misc ------------------------------------------------------------------
  async get_auth_token() {
    return store.loadAuthToken();
  },
  async get_api_url() {
    return api.apiBase();
  },
  async report_crash(args) {
    await api.reportCrash(args.message, args.stack ?? "", APP_VERSION, "web");
  },

  // Web Push ---------------------------------------------------------------
  // The browser-side subscribe/unsubscribe lives in lib/push.ts; these commands
  // are the server half (storing/clearing the subscription on the device).
  async register_push(args) {
    await api.savePushSubscription(await requireAuth(), args.subscription as PushSubscriptionJSON);
  },
  async unregister_push() {
    await api.deletePushSubscription(await requireAuth());
  },
};

/** Drop-in replacement for @tauri-apps/api/core `invoke`. */
export async function invoke<T = unknown>(command: string, args: Args = {}): Promise<T> {
  const handler = handlers[command];
  if (!handler) throw new Error(`Unknown command: ${command}`);
  return (await handler(args)) as T;
}

/** Retry queued offline updates; call on reconnect. Stops on the first failure (still offline). */
export async function flushOutbox(): Promise<void> {
  const token = await store.loadAuthToken();
  if (!token) return;
  let groupKey: Uint8Array | null = null;
  for (const row of await store.listOutbox()) {
    try {
      let encryptedTitle = row.encryptedTitle;
      if (encryptedTitle == null) {
        // Rows queued before the Phase-4 deploy carry a plaintext `docTitle` and no
        // `encryptedTitle`; encrypt it on the fly so the push isn't rejected (missing title).
        groupKey ??= await store.loadGroupKey();
        encryptedTitle = await encryptText(groupKey, (row as { docTitle?: string }).docTitle ?? "Untitled");
      }
      await api.pushSync(token, row.docId, row.docType, encryptedTitle, row.encryptedB64);
      if (row.id != null) await store.deleteOutbox(row.id);
    } catch {
      break;
    }
  }
}
